/**
 * Shared chrome for the token-linked handoff pages (landing, success, error).
 *
 * These pages are opened from a text message, almost always on a phone, and
 * they used to each hand-roll their own full-page gradient plus a `dark:`
 * override for every single colour. This centralizes the frame on the app's
 * theme tokens so the whole flow reads as one surface and dark mode comes for
 * free: one soft amber wash at the top, one card, one type scale.
 */

import React from 'react';
import { Button } from '@crisellevaldez/speculo-ui';
import { cn } from '../../../utils/cn';

/** Accent used by the outcome medallion and its glow. */
export type HandoffTone = 'emerald' | 'amber' | 'blue' | 'rose';

const TONE_MEDALLION: Record<HandoffTone, string> = {
  emerald: 'bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-emerald-500/25',
  amber: 'bg-gradient-to-br from-amber-400 to-orange-500 shadow-amber-500/25',
  blue: 'bg-gradient-to-br from-blue-400 to-indigo-500 shadow-blue-500/25',
  rose: 'bg-gradient-to-br from-rose-400 to-rose-600 shadow-rose-500/25',
};

interface HandoffPageShellProps {
  children: React.ReactNode;
  /**
   * Tailwind max-width classes. Defaults step up with the viewport so the card
   * is not the same physical size on a phone and a 27" monitor; the landing page
   * overrides this with a wider ramp.
   */
  maxWidth?: string;
}

export const HandoffPageShell: React.FC<HandoffPageShellProps> = ({
  children,
  maxWidth = 'max-w-sm sm:max-w-md lg:max-w-lg',
}) => (
  // No background of its own: in light mode `--background` and `--card` are both
  // pure white, so painting it here would flatten the card against the page.
  // The app body already supplies the warm cream / dark radial canvas.
  <div className="relative min-h-screen">
    {/* Single ambient wash, in place of the old per-page gradient backgrounds. */}
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-0 top-0 h-96 bg-[radial-gradient(ellipse_70%_100%_at_50%_0%,rgba(251,191,36,0.26),transparent_70%)] dark:bg-[radial-gradient(ellipse_70%_100%_at_50%_0%,rgba(251,191,36,0.10),transparent_70%)]"
    />
    <div className="relative flex min-h-screen items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className={cn('w-full', maxWidth)}>{children}</div>
    </div>
  </div>
);

interface HandoffCardProps {
  children: React.ReactNode;
  className?: string;
}

/** The one card style these pages share. */
export const HandoffCard: React.FC<HandoffCardProps> = ({ children, className }) => (
  <div
    className={cn(
      'overflow-hidden rounded-2xl border border-foreground/10 bg-card lg:rounded-3xl shadow-[0_1px_2px_rgba(0,0,0,0.04),0_18px_44px_-26px_rgba(0,0,0,0.30)]',
      className
    )}
  >
    {children}
  </div>
);

export interface HandoffAction {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  variant?: 'primary' | 'outline';
}

interface HandoffOutcomeProps {
  tone: HandoffTone;
  icon: React.ReactNode;
  title: string;
  message: React.ReactNode;
  actions: HandoffAction[];
  /** Rendered above the medallion — used for the "gotta be quicker" GIF. */
  media?: React.ReactNode;
}

/**
 * Terminal-state card: medallion, headline, one paragraph, stacked actions.
 * Success, error, and the already-claimed page are all this shape, so they now
 * share it instead of repeating ~40 lines of markup each.
 */
export const HandoffOutcome: React.FC<HandoffOutcomeProps> = ({
  tone,
  icon,
  title,
  message,
  actions,
  media,
}) => (
  <HandoffCard>
    {media}
    <div className="px-5 py-6 text-center sm:px-8 sm:py-9 lg:px-10 lg:py-11">
      <div
        className={cn(
          'mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg sm:mb-5 sm:h-16 sm:w-16 lg:h-[4.5rem] lg:w-[4.5rem]',
          '[&>svg]:h-7 [&>svg]:w-7 sm:[&>svg]:h-8 sm:[&>svg]:w-8 lg:[&>svg]:h-9 lg:[&>svg]:w-9',
          TONE_MEDALLION[tone]
        )}
      >
        {icon}
      </div>

      <h1 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl lg:text-3xl">
        {title}
      </h1>
      <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground sm:mt-2.5 sm:text-base lg:max-w-md">
        {message}
      </p>

      <div className="mt-6 flex flex-col gap-2.5 sm:mt-7">
        {actions.map((action) => (
          <Button
            key={action.label}
            variant={action.variant ?? 'outline'}
            onClick={action.onClick}
            className="w-full"
          >
            {action.icon}
            {action.label}
          </Button>
        ))}
      </div>
    </div>
  </HandoffCard>
);

/** Small uppercase section label used down the landing page. */
export const HandoffSectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground sm:text-xs">
    {children}
  </p>
);

export default HandoffPageShell;
